import React, { useState } from "react";
import {
  Box, Card, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography, IconButton, Chip, TextField,
  InputAdornment, Tooltip, Paper, Stack, useTheme, TablePagination,
} from "@mui/material";
import { Edit, Delete, Search, Refresh, Visibility } from "@mui/icons-material";
import { useCollection } from "../../hooks/useCollection";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import EmptyState from "../../components/ui/EmptyState";
import ConfirmDialog from "../../components/ui/ConfirmDialog";

const JournalEntryList = ({ onView, onEdit }) => {
  const theme = useTheme();
  const { data: entries, loading } = useCollection("journalEntries");
  const [localEntries, setLocalEntries] = useState(null);
  const { remove } = useAuditFirestore("journalEntries");
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  React.useEffect(() => {
    if (entries) setLocalEntries(entries);
  }, [entries]);

  React.useEffect(() => {
    setPage(0);
  }, [search]);

  const filtered = localEntries
    ?.filter((e) => e.isActive !== false)
    ?.filter((e) =>
      search === "" ||
      e.concept?.toLowerCase().includes(search.toLowerCase()) ||
      e.entryNumber?.toLowerCase().includes(search.toLowerCase())
    )
    ?.sort((a, b) => new Date(b.date) - new Date(a.date));

  const paginated = filtered?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const getTotalDebit = (entry) => {
    return entry.lines?.reduce((sum, line) => sum + (line.debit || 0), 0) || 0;
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setLocalEntries((prev) => prev?.filter((e) => e.id !== deleteTarget.id));
    const toDelete = deleteTarget;
    setDeleteTarget(null);
    try { await remove(toDelete.id); }
    catch (error) { console.error("Error deleting:", error); setLocalEntries((prev) => [...prev, toDelete]); }
  };

  if (loading || !localEntries) return <LoadingSpinner />;

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: "20px", border: "1px solid", borderColor: theme.palette.divider }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="center">
          <TextField
            placeholder="Buscar asientos..."
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: { xs: "100%", sm: 350 }, "& .MuiOutlinedInput-root": { borderRadius: "12px", height: 44 } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
            }}
          />
          <Chip label={`${filtered?.length || 0} asientos`} sx={{ height: 44, borderRadius: "12px", fontWeight: 700 }} />
          <Tooltip title="Refrescar">
            <IconButton onClick={() => { if (entries) setLocalEntries(entries); }} size="small"><Refresh /></IconButton>
          </Tooltip>
        </Stack>
      </Paper>

      {filtered?.length === 0 ? (
        <EmptyState title="Sin asientos" description="Crea tu primer asiento contable" />
      ) : (
        <Card elevation={2} sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid", borderColor: theme.palette.divider }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Nº</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Concepto</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Importe</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, width: 130 }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginated.map((entry) => (
                  <TableRow key={entry.id} hover onClick={() => onView(entry)} sx={{ cursor: "pointer" }}>
                    <TableCell sx={{ fontWeight: 600 }}>{entry.entryNumber || '-'}</TableCell>
                    <TableCell>{new Date(entry.date).toLocaleDateString('es-ES')}</TableCell>
                    <TableCell>{entry.concept}</TableCell>
                    <TableCell>
                      <Chip label={entry.isManual ? 'Manual' : 'Automático'} size="small" color={entry.isManual ? 'primary' : 'default'} variant="outlined" />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{getTotalDebit(entry).toFixed(2)}€</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Ver"><IconButton size="small" onClick={(e) => { e.stopPropagation(); onView(entry); }} color="info"><Visibility fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Editar"><IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(entry); }} color="primary"><Edit fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Eliminar"><IconButton size="small" onClick={(e) => { e.stopPropagation(); setDeleteTarget(entry); }} color="error"><Delete fontSize="small" /></IconButton></Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filtered?.length || 0}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </Card>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Eliminar Asiento"
        message={`¿Eliminar asiento ${deleteTarget?.entryNumber || ''}?`}
        type="delete"
      />
    </Box>
  );
};

export default JournalEntryList;